import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDashboard, useResource } from "@/hooks/useResource";
import { KpiCard } from "@/components/common/KpiCard";
import { RevenueArea } from "@/components/common/Charts";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Skeletons } from "@/components/common/EmptyState";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sparkle, RocketLaunch, SealCheck, Warning, Fire, TrendUp, ArrowRight } from "@phosphor-icons/react";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const { items: activity } = useResource("activity");
  const { items: campaigns } = useResource("campaigns");

  const upcoming = campaigns.filter((c) => ["Scheduled", "Planning", "Approved"].includes(c.status)).slice(0, 4);

  return (
    <div className="space-y-6">
      {/* Hero briefing */}
      <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-brand-violet/20 via-surface-raised to-brand-pink/10 p-8" style={{ animation: "fade-up 0.4s ease-out both" }}>
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-violet/30 blur-3xl" />
        <div className="absolute right-24 top-10 h-28 w-28 rounded-full bg-brand-pink/20 blur-3xl" />
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-violet">{greeting()}, {user?.name?.split(" ")[0]} 👋</p>
        <h1 className="mt-2 max-w-2xl font-display text-3xl font-black leading-tight sm:text-4xl">
          Your marketing engine is humming. Here's today's briefing.
        </h1>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => navigate("/studio")} data-testid="briefing-studio" className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur transition-transform hover:-translate-y-0.5">
            <Sparkle size={16} weight="fill" className="text-brand-yellow" /> Generate content
          </button>
          <button onClick={() => navigate("/campaigns")} className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur transition-transform hover:-translate-y-0.5">
            <RocketLaunch size={16} weight="fill" className="text-brand-mint" /> New campaign
          </button>
          <button onClick={() => navigate("/insights")} className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur transition-transform hover:-translate-y-0.5">
            <TrendUp size={16} weight="fill" className="text-brand-lime" /> Ask AI Insights
          </button>
        </div>
      </div>

      {/* Quick stat chips */}
      {data && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Live campaigns", value: data.counts.live_campaigns, icon: Fire, color: "#A3E635" },
            { label: "Upcoming launches", value: data.counts.upcoming_launches, icon: RocketLaunch, color: "#7C3AED" },
            { label: "Pending approvals", value: data.counts.pending_approvals, icon: SealCheck, color: "#FACC15" },
            { label: "Overdue tasks", value: data.counts.overdue_tasks, icon: Warning, color: "#FB7185" },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="flex items-center gap-3 rounded-2xl border border-border bg-surface-raised p-4" style={{ animation: `fade-up 0.4s ease-out ${i * 0.05}s both` }}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${c.color}22`, color: c.color }}>
                  <Icon size={22} weight="duotone" />
                </div>
                <div>
                  <p className="font-display text-2xl font-black">{c.value}</p>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isLoading ? <Skeletons /> : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          {data?.kpis?.slice(0, 4).map((k, i) => <KpiCard key={k.label} {...k} delay={i * 0.05} />)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Trend */}
        <div className="rounded-2xl border border-border bg-surface-raised p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Revenue vs Spend</h2>
            <span className="rounded-full bg-lime-500/15 px-2.5 py-1 text-xs font-semibold text-lime-400">+12.4% MoM</span>
          </div>
          {data && <RevenueArea data={data.trend} />}
        </div>

        {/* Activity feed */}
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Team Activity</h2>
          <div className="space-y-4">
            {activity.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-start gap-3" data-testid="activity-item">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={a.avatar} />
                  <AvatarFallback>{a.user?.[0]}</AvatarFallback>
                </Avatar>
                <p className="text-sm leading-snug"><span className="font-semibold">{a.user}</span> <span className="text-muted-foreground">{a.action}</span></p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming campaigns */}
      <div className="rounded-2xl border border-border bg-surface-raised p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Upcoming Campaigns</h2>
          <button onClick={() => navigate("/campaigns")} className="flex items-center gap-1 text-sm font-semibold text-primary">View all <ArrowRight size={14} /></button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {upcoming.map((c) => (
            <div key={c.id} onClick={() => navigate("/campaigns")} className="cursor-pointer rounded-xl border border-border bg-surface p-4 transition-transform hover:-translate-y-1">
              <div className="mb-2 flex items-center justify-between">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                <StatusBadge status={c.status} />
              </div>
              <p className="font-semibold leading-tight">{c.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{c.brand} · {c.objective}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
